"use client";

import { useMemo, useState } from "react";
import clsx from "clsx";
import { unpackRows, type PackedRows } from "@/lib/screener/transport";
import type { RsGrowthRow } from "@/lib/screener/types";
import { DataTable, type Column } from "./data-table";
import {
  FilterBuilder,
  applyRules,
  type FilterField,
  type FilterRule,
} from "./filter-builder";
import { CountBadge, McapSelect, SearchBox, TickerCell } from "./controls";
import {
  distinct,
  fmt,
  fmtSignedPct,
  formatMcap,
  getMacdStatus,
  getRSColorStyle,
  getYtdReturn,
  matchMcapFilter,
  type MacdStatus,
} from "./logic";

type GrowthView = RsGrowthRow & {
  ytd: number | null;
  wk_macd: MacdStatus | null;
  mo_macd: MacdStatus | null;
  day_macd_bull: boolean;
};

const MACD_OPTS: MacdStatus[] = [
  "Above & Bullish",
  "Above & Bearish",
  "Under & Bullish",
  "Under & Bearish",
];

const MACD_CLS: Record<MacdStatus, string> = {
  "Above & Bullish": "border-up/30 bg-up/10 text-up",
  "Above & Bearish": "border-orange-500/30 bg-orange-500/10 text-orange-400",
  "Under & Bullish": "border-sky-500/30 bg-sky-500/10 text-sky-400",
  "Under & Bearish": "border-down/30 bg-down/10 text-down",
};

function MacdBadge({ s }: { s: MacdStatus | null }) {
  if (!s) return <span className="font-mono text-[10px] text-muted">—</span>;
  return (
    <span
      className={clsx(
        "rounded border px-2 py-0.5 text-[10px] font-semibold",
        MACD_CLS[s],
      )}
    >
      {s}
    </span>
  );
}

function RsHeat({ v }: { v: number | null }) {
  return (
    <span
      className="inline-block min-w-9 rounded px-1.5 py-0.5 text-center font-mono"
      style={getRSColorStyle(v)}
    >
      {v ?? "—"}
    </span>
  );
}

export function RsGrowthTab({ data }: { data: PackedRows<RsGrowthRow> }) {
  const rows = useMemo(() => unpackRows(data), [data]);
  const [search, setSearch] = useState("");
  const [mcapFilter, setMcapFilter] = useState("All");
  const [starsBadge, setStarsBadge] = useState<number | null>(null);
  const [rules, setRules] = useState<FilterRule[]>([]);

  // stockweb's SQL only returns `stars > 0` ordered by stars DESC, rs_rating
  // DESC NULLS LAST — re-applied here so the default view is identical even
  // if the stored base order is lost.
  const view = useMemo<GrowthView[]>(
    () =>
      rows
        .filter((r) => r.stars > 0)
        .map((r) => ({
          ...r,
          ytd: getYtdReturn(r.close, r.ytd_start_price),
          wk_macd: getMacdStatus(r.wk_macd_line, r.wk_macd_signal),
          mo_macd: getMacdStatus(r.mo_macd_line, r.mo_macd_signal),
          day_macd_bull:
            r.daily_macd_line != null &&
            r.daily_macd_signal != null &&
            r.daily_macd_line > 0 &&
            r.daily_macd_line > r.daily_macd_signal,
        }))
        .sort(
          (a, b) =>
            b.stars - a.stars ||
            (b.rs_rating ?? -Infinity) - (a.rs_rating ?? -Infinity),
        ),
    [rows],
  );

  const fields = useMemo<FilterField<GrowthView>[]>(
    () => [
      {
        id: "stars",
        label: "Stars (1–3)",
        kind: "number",
        get: (r) => r.stars,
      },
      {
        id: "rs_rating",
        label: "RS Rating",
        kind: "number",
        get: (r) => r.rs_rating,
      },
      {
        id: "rs_rating_sector",
        label: "Sector RS",
        kind: "number",
        get: (r) => r.rs_rating_sector,
      },
      { id: "ytd", label: "YTD (%)", kind: "number", get: (r) => r.ytd },
      { id: "rs_5d", label: "RS-5D", kind: "number", get: (r) => r.rs_5d },
      { id: "rs_14d", label: "RS-14D", kind: "number", get: (r) => r.rs_14d },
      { id: "rs_30d", label: "RS-30D", kind: "number", get: (r) => r.rs_30d },
      { id: "rs_60d", label: "RS-60D", kind: "number", get: (r) => r.rs_60d },
      { id: "rs_90d", label: "RS-90D", kind: "number", get: (r) => r.rs_90d },
      {
        id: "vol_ratio",
        label: "Volume ratio",
        kind: "number",
        get: (r) => r.vol_ratio,
      },
      { id: "close", label: "Close", kind: "number", get: (r) => r.close },
      { id: "ema_50", label: "EMA50", kind: "number", get: (r) => r.ema_50 },
      { id: "ema_200", label: "EMA200", kind: "number", get: (r) => r.ema_200 },
      {
        id: "mcap_b",
        label: "Market cap ($B)",
        kind: "number",
        get: (r) => (r.market_cap == null ? null : r.market_cap / 1e9),
      },
      {
        id: "wk_macd",
        label: "MACD Weekly",
        kind: "enum",
        options: MACD_OPTS,
        get: (r) => r.wk_macd,
      },
      {
        id: "mo_macd",
        label: "MACD Monthly",
        kind: "enum",
        options: MACD_OPTS,
        get: (r) => r.mo_macd,
      },
      {
        id: "day_macd_bull",
        label: "Daily MACD > 0 & > Signal",
        kind: "enum",
        options: ["true", "false"],
        get: (r) => r.day_macd_bull,
      },
      { id: "ticker", label: "Ticker", kind: "text", get: (r) => r.ticker },
      {
        id: "company_name",
        label: "Company",
        kind: "text",
        get: (r) => r.company_name,
      },
      {
        id: "sector",
        label: "Sector",
        kind: "enum",
        options: distinct(rows.map((r) => r.sector)),
        get: (r) => r.sector,
      },
      {
        id: "industry",
        label: "Industry",
        kind: "text",
        get: (r) => r.industry,
      },
    ],
    [rows],
  );

  // stockweb `filteredGrowth`: mcap + RS / Sector RS number filters (as rules).
  // Search is an addition (stockweb's RS Growth tab had none).
  const base = useMemo(() => {
    const s = search.trim().toUpperCase();
    return applyRules(view, rules, fields)
      .filter((r) => matchMcapFilter(r.market_cap, mcapFilter))
      .filter(
        (r) =>
          !s ||
          r.ticker.includes(s) ||
          r.sector?.toUpperCase().includes(s) ||
          r.company_name?.toUpperCase().includes(s),
      );
  }, [view, rules, fields, mcapFilter, search]);

  const filtered = useMemo(
    () =>
      starsBadge == null ? base : base.filter((r) => r.stars === starsBadge),
    [base, starsBadge],
  );

  const rsCol = (
    id:
      | "rs_rating"
      | "rs_rating_sector"
      | "rs_5d"
      | "rs_14d"
      | "rs_30d"
      | "rs_60d"
      | "rs_90d",
    label: string,
  ): Column<GrowthView> => ({
    id,
    label,
    align: "center",
    sortValue: (r) => r[id],
    cell: (r) => <RsHeat v={r[id]} />,
  });

  const columns: Column<GrowthView>[] = [
    {
      id: "stars",
      label: "Watchlist",
      align: "center",
      sortValue: (r) => r.stars,
      cell: (r) => (
        <span className="font-bold text-amber-400" title={`${r.stars} ดาว`}>
          {"★".repeat(r.stars)}
        </span>
      ),
    },
    {
      id: "ticker",
      label: "Ticker",
      pinned: true,
      sortValue: (r) => r.ticker,
      cell: (r) => <TickerCell ticker={r.ticker} />,
    },
    {
      id: "company_name",
      label: "Company",
      defaultHidden: true,
      cell: (r) => (
        <div className="max-w-[160px] truncate text-xs text-muted">
          {r.company_name ?? "—"}
        </div>
      ),
    },
    {
      id: "close",
      label: "Close",
      align: "right",
      sortValue: (r) => r.close,
      cell: (r) => <span className="font-mono">${fmt(r.close)}</span>,
    },
    {
      id: "ytd",
      label: "YTD",
      align: "center",
      sortValue: (r) => r.ytd,
      cell: (r) => (
        <span
          className={clsx(
            "font-mono font-semibold",
            r.ytd == null ? "text-muted" : r.ytd >= 0 ? "text-up" : "text-down",
          )}
        >
          {fmtSignedPct(r.ytd, 2)}
        </span>
      ),
    },
    rsCol("rs_rating", "RS Rating"),
    rsCol("rs_rating_sector", "Sector RS"),
    rsCol("rs_5d", "RS-5D"),
    rsCol("rs_14d", "RS-14D"),
    rsCol("rs_30d", "RS-30D"),
    rsCol("rs_60d", "RS-60D"),
    rsCol("rs_90d", "RS-90D"),
    {
      id: "ema_50",
      label: "EMA50",
      align: "right",
      sortValue: (r) => r.ema_50,
      cell: (r) => (
        <span className="font-mono text-orange-400/90">${fmt(r.ema_50)}</span>
      ),
    },
    {
      id: "ema_200",
      label: "EMA200",
      align: "right",
      sortValue: (r) => r.ema_200,
      cell: (r) => (
        <span className="font-mono text-purple-400/90">${fmt(r.ema_200)}</span>
      ),
    },
    {
      id: "vol_ratio",
      label: "Volume Ratio",
      align: "center",
      sortValue: (r) => r.vol_ratio,
      cell: (r) => {
        const v = r.vol_ratio;
        if (v == null) return <span className="text-muted">—</span>;
        return (
          <span
            className={clsx(
              "font-mono",
              v >= 1.5
                ? "font-bold text-up"
                : v >= 1.0
                  ? "text-sky-400"
                  : "text-muted",
            )}
          >
            {v.toFixed(2)}x
          </span>
        );
      },
    },
    {
      id: "wk_macd",
      label: "MACD Weekly",
      align: "center",
      sortValue: (r) => r.wk_macd,
      cell: (r) => <MacdBadge s={r.wk_macd} />,
    },
    {
      id: "mo_macd",
      label: "MACD Monthly",
      align: "center",
      sortValue: (r) => r.mo_macd,
      cell: (r) => <MacdBadge s={r.mo_macd} />,
    },
    {
      id: "sector",
      label: "Sector",
      defaultHidden: true,
      sortValue: (r) => r.sector,
      cell: (r) => <span className="text-xs">{r.sector ?? "—"}</span>,
    },
    {
      id: "industry",
      label: "Industry",
      defaultHidden: true,
      sortValue: (r) => r.industry,
      cell: (r) => (
        <span className="text-xs text-muted">{r.industry ?? "—"}</span>
      ),
    },
    {
      id: "market_cap",
      label: "Market cap",
      align: "right",
      defaultHidden: true,
      sortValue: (r) => r.market_cap,
      cell: (r) => (
        <span className="font-mono text-xs">{formatMcap(r.market_cap)}</span>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <FilterBuilder
        fields={fields}
        rules={rules}
        onChange={setRules}
        quickFilters={
          <>
            {[3, 2, 1].map((n) => (
              <CountBadge
                key={n}
                tone="amber"
                active={starsBadge === n}
                onClick={() => setStarsBadge((cur) => (cur === n ? null : n))}
              >
                {"★".repeat(n)}: {base.filter((r) => r.stars === n).length}
              </CountBadge>
            ))}
          </>
        }
        controls={
          <>
            <SearchBox
              value={search}
              onChange={setSearch}
              placeholder="ค้นหา ticker / sector / company…"
            />
            <McapSelect value={mcapFilter} onChange={setMcapFilter} />
          </>
        }
      />

      <DataTable
        rows={filtered}
        columns={columns}
        rowKey={(r) => r.ticker}
        defaultSort={{ id: "stars", asc: false }}
        emptyText="ไม่มีหุ้นที่ตรงเงื่อนไข (1-3 ดาว)"
        toolbar={
          <span>
            แสดง{" "}
            <span className="font-medium text-foreground">
              {filtered.length}
            </span>{" "}
            จาก {view.length} หุ้นที่มี 1-3 ดาว
          </span>
        }
      />
    </div>
  );
}
